import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, MessageSquare, Send } from "lucide-react";
import { API, authHeaders } from "@/constants/roles";

// Hiring conversation threaded on one application. variant "staff": owner/manager console;
// variant "applicant": the applicant's own dashboard. Polls every 15s while mounted.
export const ApplicantChat = ({ applicationId = null, variant = "staff", applicantName = "" }) => {
  const [messages, setMessages] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [missing, setMissing] = useState(false);
  const bottomRef = useRef(null);

  const url =
    variant === "staff"
      ? `${API}/staff/applications/${applicationId}/chat`
      : `${API}/my/application/chat`;

  const load = useCallback(() => {
    axios
      .get(url, { headers: authHeaders() })
      .then((r) => setMessages(r.data.messages || []))
      .catch((e) => {
        if (e.response?.status === 404) setMissing(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      await axios.post(url, { text: text.trim() }, { headers: authHeaders() });
      setText("");
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Message failed to send.");
    } finally {
      setBusy(false);
    }
  };

  if (missing) return null;

  const mine = (m) => (variant === "staff" ? m.sender === "staff" : m.sender === "applicant");

  return (
    <div data-testid={variant === "staff" ? `applicant-chat-${applicationId}` : "applicant-chat"} className="rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-subtle)]">
      <div className="flex items-center gap-2 border-b border-[var(--pv-border)] px-4 py-3">
        <MessageSquare className="h-4 w-4 text-cyan-400" />
        <p className="text-xs font-bold uppercase tracking-wider text-[var(--pv-text-4)]">
          {variant === "staff" ? `Conversation with ${applicantName || "applicant"}` : "Messages with the Melto Studios team"}
        </p>
      </div>
      <div data-testid="chat-messages" className="max-h-72 space-y-2.5 overflow-y-auto px-4 py-3">
        {!messages ? (
          <p className="py-6 text-center"><Loader2 className="mx-auto h-4 w-4 animate-spin text-cyan-400" /></p>
        ) : messages.length === 0 ? (
          <p data-testid="chat-empty" className="py-6 text-center text-xs text-[var(--pv-text-4)]">
            {variant === "staff"
              ? "No messages yet — start the conversation to clarify expectations before deciding."
              : "No messages yet. If the team reaches out about your application, it appears here — you can reply below."}
          </p>
        ) : (
          messages.map((m) => (
            <div key={m.id} data-testid={`chat-msg-${m.id}`} className={`flex ${mine(m) ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-xs leading-relaxed ${mine(m) ? "bg-cyan-500 text-white" : "border border-[var(--pv-border)] bg-[var(--pv-card)] text-[var(--pv-text-2)]"}`}>
                <p className={`mb-0.5 text-[9px] font-bold uppercase tracking-wider ${mine(m) ? "text-cyan-100/80" : "text-[var(--pv-text-4)]"}`}>
                  {mine(m) ? "You" : m.sender_name || (m.sender === "staff" ? "Melto Studios" : applicantName || "Applicant")}
                  {" · "}
                  {new Date(m.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                </p>
                <p className="whitespace-pre-wrap">{m.text}</p>
              </div>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>
      <form onSubmit={send} className="flex items-center gap-2 border-t border-[var(--pv-border)] px-3 py-2.5">
        <input
          data-testid="chat-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={variant === "staff" ? `Message ${applicantName || "the applicant"}…` : "Reply to the team…"}
          maxLength={4000}
          className="min-w-0 flex-1 rounded-full border border-[var(--pv-border)] bg-[var(--pv-bg)] px-4 py-2 text-xs font-medium text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:border-cyan-400 focus:outline-none"
        />
        <button data-testid="chat-send-btn" type="submit" disabled={busy || !text.trim()} className="flex items-center gap-1.5 rounded-full bg-cyan-500 px-4 py-2 text-xs font-bold text-white hover:bg-cyan-600 disabled:opacity-50">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send
        </button>
      </form>
    </div>
  );
};
